import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Video } from "@/types/video";

export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setFavorites([]);
      setIsLoading(false);
      return;
    }

    const fetchFavorites = async () => {
      try {
        const { data, error } = await supabase
          .from("user_favorites")
          .select(`
            video_id,
            videos (
              id,
              title,
              description,
              thumbnail_url,
              video_url,
              duration,
              view_count,
              like_count,
              category,
              jlpt_level,
              created_at,
              updated_at,
              created_by,
              is_published,
              is_premium
            )
          `)
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (error) throw error;

        // Transform the data to match Video type and filter out null videos
        const videos: Video[] = data
          .filter((item: any) => item.videos) // Filter out null videos
          .map((item: any) => ({
            id: item.videos.id,
            title: item.videos.title,
            description: item.videos.description,
            thumbnailUrl: item.videos.thumbnail_url,
            videoUrl: item.videos.video_url,
            duration: item.videos.duration,
            viewCount: item.videos.view_count,
            likeCount: item.videos.like_count,
            category: item.videos.category,
            jlptLevel: item.videos.jlpt_level,
            createdAt: item.videos.created_at,
            updatedAt: item.videos.updated_at,
            createdBy: item.videos.created_by,
            isPublished: item.videos.is_published,
            isPremium: item.videos.is_premium
          }));

        setFavorites(videos);
      } catch (error) {
        console.error("Error fetching favorites:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavorites();
  }, [user]);

  return { favorites, isLoading };
}
